import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useLockContext } from '../contexts/LockContext';
import { useCurrentPerson } from '../hooks/useCurrentPerson';
import { getCurrentUser } from '@/db/services/user_service';
import { getItem, setItem } from '@/db/services/application_session_service';
import { logout } from '../lib/auth';
import { isOnline as checkServerOnline, checkConnectivity } from '../lib/connectivity';
import { isAppInstalled } from '@/db/services/analytics_service';
import { MONAMI_HOST, BIOMETRICS_DISABLED, INSTALL_GUIDE_URL } from '../config/env';
import { officialFullName } from '@/db/types/person';
import { Button } from '@mes-amis/galette-design-system';
import { ExclamationTriangleIcon, ChevronLeftIcon, ChevronRightIcon, ArrowUpRightIcon, ArrowPathIcon } from '@heroicons/react/24/outline';
import { dispatchDataEvent, DataEvents } from '@/db/events';
import { useSyncActivity } from '../hooks/useSyncActivity';
import { createLogger, LogLevel } from '../lib/logger';

const sidebarLogger = createLogger('Sidebar', LogLevel.WARN);

type NavigationItem = {
  label: string;
  href: string;
  icon: string;
  selectedIcon: string;
  matchPrefix?: boolean;
};

const navigation: NavigationItem[] = [
  {
    label: 'Dashboard',
    href: '/',
    icon: '/icons/sidebar/home.svg',
    selectedIcon: '/icons/sidebar/home-filled.svg',
  },
  {
    label: 'My Clients',
    href: '/clients',
    icon: '/icons/sidebar/all-clients.svg',
    selectedIcon: '/icons/sidebar/all-clients-filled.svg',
    matchPrefix: true,
  },
  {
    label: 'Settings',
    href: '/settings',
    icon: '/icons/sidebar/settings.svg',
    selectedIcon: '/icons/sidebar/settings-filled.svg',
  },
];

function UserAvatar({ initials, size = 'lg' }: { initials: string; size?: 'sm' | 'lg' }) {
  const dim = size === 'lg' ? 'w-9 h-9' : 'w-7 h-7';
  const text = size === 'lg' ? 'text-xs' : 'text-[9px]';
  return (
    <div className={`${dim} rounded-full bg-[#c8e6e7] flex items-center justify-center ${text} font-semibold text-[#006468] shrink-0 select-none`}>
      {initials}
    </div>
  );
}

interface ProfileDropdownProps {
  userFullName: string;
  userInitials: string;
  userEmail: string | undefined;
  orgName: string;
  personId: number;
  organizationId: number;
  isMonAmiAdmin: boolean;
  hasMultipleOrganizations: boolean;
  isRegistered: boolean;
  lockScreen: () => void;
  isExpanded: boolean;
}

function ProfileDropdown({
  userFullName,
  userInitials,
  userEmail,
  orgName,
  personId,
  organizationId,
  isMonAmiAdmin,
  hasMultipleOrganizations,
  isRegistered,
  lockScreen,
  isExpanded,
}: ProfileDropdownProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const [logoutConfirmation, setLogoutConfirmation] = useState('');
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const [copiedDetails, setCopiedDetails] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  const showDropdown = () => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    setOpen(true);
  };

  const hideDropdown = () => {
    timeoutRef.current = setTimeout(() => setOpen(false), 150);
  };

  useEffect(() => {
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, []);

  const copyImpersonateDetails = async () => {
    const details = [
      `Current page URL: ${window.location.href}`,
      `Active Admin person URL: ${MONAMI_HOST}/active_admin/people/${personId}`,
      `Organization: ${orgName}`,
      `Active Admin organization URL: ${MONAMI_HOST}/active_admin/organizations/${organizationId}/edit`,
    ].join('\n');

    try {
      if (window.location.protocol === 'https:') {
        await navigator.clipboard.writeText(details);
      } else {
        const textArea = document.createElement('textarea');
        textArea.value = details;
        textArea.style.position = 'fixed';
        textArea.style.left = '-999999px';
        textArea.style.top = '-999999px';
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        try {
          document.execCommand('copy');
        } finally {
          document.body.removeChild(textArea);
        }
      }
      setCopiedDetails(true);
      setTimeout(() => setCopiedDetails(false), 2000);
    } catch (err) {
      console.error('Failed to copy impersonate details:', err);
    }
  };

  const handleLogout = () => {
    setOpen(false);
    setShowLogoutModal(true);
    setLogoutConfirmation('');
  };

  const confirmLogout = async () => {
    if (logoutConfirmation.toLowerCase() !== 'logout and wipe device') return;
    setIsLoggingOut(true);
    await logout();
  };

  return (
    <div
      ref={containerRef}
      className="relative"
      onMouseEnter={showDropdown}
      onMouseLeave={hideDropdown}
    >
      {/* Trigger: user avatar / info */}
      <div
        className={`flex items-center gap-2 px-1 py-2 rounded cursor-pointer hover:bg-white/10 transition-colors`}
      >
        <UserAvatar initials={userInitials} size="lg" />
        {isExpanded && (
          <div className="flex flex-col min-w-0 flex-1 text-[11px] leading-[12px] text-white">
            <span className="font-semibold truncate">{userFullName}</span>
            <span className="font-normal truncate opacity-80">{orgName}</span>
          </div>
        )}
      </div>

      {/* Dropdown — opens upward */}
      {open && (
        <div
          className={`absolute bottom-full mb-2 bg-white rounded shadow-lg border border-gray-200 z-50 min-w-[240px] ${
            isExpanded ? 'left-0' : 'left-0'
          }`}
        >
          {/* User info */}
          <div className="flex items-center gap-3 px-3 py-3 border-b border-gray-100">
            <UserAvatar initials={userInitials} size="lg" />
            <div className="flex flex-col min-w-0 gap-1">
              <span className="text-sm text-gray-900 truncate">{userFullName}</span>
              {userEmail && (
                <span className="text-xs text-gray-500 truncate">{userEmail}</span>
              )}
              <span className="text-sm font-semibold text-gray-900 truncate">{orgName}</span>
            </div>
          </div>

          {/* Actions */}
          <div className="py-1">
            {hasMultipleOrganizations && (
              <button
                onClick={() => { setOpen(false); navigate('/select-organization'); }}
                className="w-full text-left px-3 py-2 text-xs font-semibold text-[#5e6fcf] hover:bg-gray-50 transition-colors"
              >
                Switch Organization
              </button>
            )}

            {isRegistered && !BIOMETRICS_DISABLED && (
              <button
                onClick={() => { setOpen(false); lockScreen(); }}
                className="w-full text-left px-3 py-2 text-xs font-semibold text-gray-700 hover:bg-gray-50 transition-colors"
              >
                Lock Screen
              </button>
            )}

            {isMonAmiAdmin && (
              <button
                onClick={() => void copyImpersonateDetails()}
                className="w-full text-left px-3 py-2 text-xs font-semibold text-gray-700 hover:bg-gray-50 transition-colors"
              >
                {copiedDetails ? 'Copied!' : 'Copy Impersonate Details'}
              </button>
            )}

            <a
              href={`${MONAMI_HOST}/`}
              target="_blank"
              rel="noopener noreferrer"
              className="block w-full text-left px-3 py-2 text-xs font-semibold text-gray-700 hover:bg-gray-50 transition-colors"
            >
              Open main A&D App
            </a>
          </div>

          {/* Log Out — dangerous action */}
          <div className="border-t border-gray-100 py-1">
            <button
              onClick={handleLogout}
              className="w-full text-left px-3 py-2 text-xs font-semibold text-red-600 hover:bg-red-50 transition-colors"
            >
              Logout and Wipe Device
            </button>
          </div>
        </div>
      )}

      {/* Logout Confirmation Modal */}
      {showLogoutModal && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 p-6">
            <div className="flex items-start gap-3 mb-4">
              <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center shrink-0">
                <ExclamationTriangleIcon className="w-5 h-5 text-red-600" />
              </div>
              <div>
                <h3 className="text-base font-semibold text-gray-900 mb-1">Logout and Wipe Device</h3>
                <p className="text-sm text-red-800">
                  This will permanently delete all local data including unsynced changes. You will need to log in again with internet connection.
                </p>
              </div>
            </div>
            <label htmlFor="sidebar-logout-confirm" className="block text-sm font-medium text-gray-900 mb-2">
              Type <code className="bg-red-100 px-1.5 py-0.5 rounded font-mono text-red-900 text-xs">logout and wipe device</code> to confirm:
            </label>
            <input
              id="sidebar-logout-confirm"
              type="text"
              value={logoutConfirmation}
              onChange={(e) => setLogoutConfirmation(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') confirmLogout(); }}
              placeholder="Type 'logout and wipe device' to confirm"
              disabled={isLoggingOut}
              className="w-full px-3 py-2 mb-4 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500 disabled:bg-gray-100"
              autoFocus
            />
            <div className="flex justify-end gap-3">
              <Button
                variant="tertiary"
                size="sm"
                onClick={() => setShowLogoutModal(false)}
                disabled={isLoggingOut}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                destructive
                size="sm"
                onClick={confirmLogout}
                disabled={logoutConfirmation.toLowerCase() !== 'logout and wipe device' || isLoggingOut}
              >
                {isLoggingOut ? 'Logging out...' : 'Logout and Wipe Device'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default function Sidebar() {
  const location = useLocation();
  const { lockScreen, isRegistered } = useLockContext();
  const { currentPerson } = useCurrentPerson();
  const [hasMultipleOrganizations, setHasMultipleOrganizations] = useState(false);
  const [userEmail, setUserEmail] = useState<string | undefined>();
  const [isMonAmiAdmin, setIsMonAmiAdmin] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [expandedLoaded, setExpandedLoaded] = useState(false);
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const { status: syncStatus } = useSyncActivity();
  const syncing = syncStatus === 'syncing';

  // Manual refresh from the sidebar. Sync is automatic; this exists for the
  // moment a user wants to see it happen. It bypasses the 15s debounce, and
  // the SyncStatusBanner reports progress and the GlobalBanner any failure.
  const refreshNow = () => {
    if (!isOnline || syncing) return;
    dispatchDataEvent(DataEvents.REFRESH, { force: true }).catch((error) => {
      sidebarLogger.warn('Manual refresh failed', error);
    });
  };
  const [appInstalled, setAppInstalled] = useState(() => isAppInstalled());

  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const mql = window.matchMedia('(display-mode: standalone)');
    const handleChange = () => setAppInstalled(isAppInstalled());
    mql.addEventListener('change', handleChange);
    return () => mql.removeEventListener('change', handleChange);
  }, []);

  // Load user data when currentPerson changes (reactive via useLiveQuery).
  // Using currentPerson as the dependency ensures we re-fetch after org
  // selection or org switch, rather than only on mount.
  useEffect(() => {
    if (!currentPerson) return;
    const loadUser = async () => {
      const user = await getCurrentUser();
      if (user) {
        setUserEmail(user.email);
        setHasMultipleOrganizations(user.people && user.people.length > 1);
        setIsMonAmiAdmin(user.mon_ami_admin || false);
      } else {
        setUserEmail(undefined);
        setHasMultipleOrganizations(false);
        setIsMonAmiAdmin(false);
      }
    };
    loadUser();
  }, [currentPerson]);

  useEffect(() => {
    getItem<boolean>('sidebar.expanded').then(val => {
      if (val !== null) setIsExpanded(val);
      setExpandedLoaded(true);
    });
  }, []);

  useEffect(() => {
    if (!expandedLoaded) return;
    setItem('sidebar.expanded', isExpanded);
  }, [isExpanded, expandedLoaded]);

  useEffect(() => {
    const handleOnline = () => {
      checkConnectivity().then(() => setIsOnline(checkServerOnline()));
    };
    const handleOffline = () => setIsOnline(false);
    const handleServerReachable = () => setIsOnline(true);
    const handleServerUnreachable = () => setIsOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    window.addEventListener('server-reachable', handleServerReachable);
    window.addEventListener('server-unreachable', handleServerUnreachable);

    // Verify actual server connectivity on mount (catches reload-while-offline)
    checkConnectivity().then(() => setIsOnline(checkServerOnline()));

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      window.removeEventListener('server-reachable', handleServerReachable);
      window.removeEventListener('server-unreachable', handleServerUnreachable);
    };
  }, []);

  const userFullName = currentPerson ? officialFullName(currentPerson) || 'User' : '';
  const userInitials = userFullName
    .split(' ')
    .filter(Boolean)
    .map((p) => p[0])
    .slice(0, 2)
    .join('')
    .toUpperCase();
  const orgName = currentPerson?.organization?.name || '';

  const isActive = (item: NavigationItem) => {
    if (item.matchPrefix) return location.pathname.startsWith(item.href);
    return location.pathname === item.href;
  };

  return (
    <div
      className={`bg-[#001e1f] flex flex-col h-full shrink-0 transition-[width] duration-200 relative z-10 ${isExpanded ? 'w-[205px]' : 'w-[60px]'}`}
    >
      {/* Header — Logo */}
      <div className={`shrink-0 flex items-center ${isExpanded ? 'h-[72px] px-4 justify-between' : 'h-[72px] justify-center'}`}>
        {isExpanded ? (
          <>
            <div className="flex flex-col">
              <img src="/ma-logo-full.svg" alt="Mon Ami" className="h-10 w-auto brightness-0 invert" />
              <span className="text-[10px] font-semibold text-white tracking-[5px] mt-1 whitespace-nowrap">Mon Ami GO</span>
            </div>
            <button
              onClick={() => setIsExpanded(false)}
              className="p-2.5 -m-1 rounded-lg hover:bg-white/10 text-white/70 hover:text-white transition-colors"
              aria-label="Collapse sidebar"
            >
              <ChevronLeftIcon className="w-4 h-4" />
            </button>
          </>
        ) : (
          <button
            onClick={() => setIsExpanded(true)}
            className="group flex items-center justify-center w-9 h-9 rounded hover:bg-white/10 transition-colors"
            aria-label="Expand sidebar"
          >
            <img src="/ma-logo.svg" alt="Mon Ami" className="h-9 w-auto brightness-0 invert group-hover:hidden" />
            <ChevronRightIcon className="w-4 h-4 hidden group-hover:block text-white/70" />
          </button>
        )}
      </div>

      {/* Navigation */}
      <nav className="flex-1 px-2 pt-2 pb-4 overflow-y-auto overflow-x-hidden">
        <div className="flex flex-col gap-1">
          {navigation.map((item) => {
            const active = isActive(item);
            return (
              <Link
                key={item.label}
                to={item.href}
                title={isExpanded ? undefined : item.label}
                className={`flex items-center gap-2 px-2 py-1.5 rounded transition-colors ${
                  active ? 'bg-[#054e50]' : 'hover:bg-white/10'
                }`}
              >
                <img
                  src={active ? item.selectedIcon : item.icon}
                  alt={item.label}
                  className="w-7 h-7 shrink-0"
                />
                {isExpanded && (
                  <span className="text-xs font-semibold text-white whitespace-nowrap">
                    {item.label}
                  </span>
                )}
              </Link>
            );
          })}
        </div>
      </nav>

      {/* Footer */}
      <div className="shrink-0 border-t border-[#757575] px-2 py-3 flex flex-col gap-1">
        {currentPerson && (
          <ProfileDropdown
            userFullName={userFullName}
            userInitials={userInitials}
            userEmail={userEmail}
            orgName={orgName}
            personId={currentPerson.id}
            organizationId={currentPerson.organization.id}
            isMonAmiAdmin={isMonAmiAdmin}
            hasMultipleOrganizations={hasMultipleOrganizations}
            isRegistered={isRegistered}
            lockScreen={lockScreen}
            isExpanded={isExpanded}
          />
        )}

        {/* Connection: a refresh control while online, an offline indicator otherwise */}
        {isOnline ? (
          <button
            type="button"
            onClick={refreshNow}
            disabled={syncing}
            aria-label={syncing ? 'Syncing with Mon Ami' : 'Sync now'}
            title={isExpanded ? undefined : syncing ? 'Syncing…' : 'Sync now'}
            className="flex items-center gap-2 px-2 py-1 rounded text-white/80 hover:text-white hover:bg-white/10 disabled:hover:bg-transparent transition-colors"
          >
            <ArrowPathIcon className={`w-6 h-6 shrink-0 m-0.5 ${syncing ? 'animate-spin' : ''}`} aria-hidden="true" />
            {isExpanded && (
              <span className="text-xs font-semibold whitespace-nowrap">{syncing ? 'Syncing…' : 'Sync now'}</span>
            )}
          </button>
        ) : (
          <div className="flex items-center gap-2 px-2 py-1 rounded" title="Offline Mode">
            <img src="/icons/sidebar/signal-offline.svg" alt="Offline" className="w-7 h-7 shrink-0" />
            {isExpanded && <span className="text-xs font-semibold text-white whitespace-nowrap">Offline Mode</span>}
          </div>
        )}

        {!appInstalled && (
          <a
            href={INSTALL_GUIDE_URL}
            target="_blank"
            rel="noopener noreferrer"
            title={isExpanded ? undefined : 'Install Offline App'}
            className={`flex items-center rounded-lg bg-[#054e50] hover:bg-[#0a6669] transition-colors text-white ${
              isExpanded ? 'justify-between gap-2 px-3 py-2.5 mt-1' : 'justify-center w-9 h-9 mx-auto mt-1'
            }`}
          >
            {isExpanded && (
              <span className="text-sm font-semibold whitespace-nowrap">Install Offline App</span>
            )}
            <ArrowUpRightIcon className="w-5 h-5 shrink-0" />
          </a>
        )}
      </div>
    </div>
  );
}
